import {
    BreadcrumbManager,
    BreadcrumbManagerState,
    Cart,
    CartState,
    Context,
    ContextState,
    DidYouMean,
    DidYouMeanState,
    FacetGenerator,
    FacetGeneratorState,
    FilterSuggestionsGenerator,
    FilterSuggestionsState,
    InstantProducts,
    InstantProductsState,
    NotifyTrigger,
    NotifyTriggerState,
    Pagination,
    PaginationState,
    ParameterManager,
    ParameterManagerState,
    ProductList,
    ProductListState,
    ProductView,
    ProductViewState,
    QueryTrigger,
    QueryTriggerState,
    RecentQueriesList,
    RecentQueriesState,
    RedirectionTrigger,
    RedirectionTriggerState,
    SearchBox,
    SearchBoxState,
    Sort,
    SortState,
    StandaloneSearchBox,
    StandaloneSearchBoxState,
    Summary,
    SummaryState,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
} from "@coveo/headless/ssr-commerce";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { isCoveoLoading } from "@NA/Utils/Coveo/CoveoUtils";
import React, { useContext } from "react";
import { connect } from "react-redux";

// Redux mapping
const mapStateToProps = (state: CustomApplicationState) => ({
    state,
});

// Interfaces
export interface CartHook {
    state: CartState | null;
    methods?: Omit<Cart, "state" | "subscribe"> | null;
}
export interface ContextHook {
    state: ContextState | null;
    methods?: Omit<Context, "state" | "subscribe"> | null;
}
export interface ProductListHook {
    state: ProductListState | null;
    methods?: Omit<ProductList, "state" | "subscribe"> | null;
}
export interface DidYouMeanHook {
    state: DidYouMeanState | null;
    methods?: Omit<DidYouMean, "state" | "subscribe"> | null;
}
export interface InstantProductsHook {
    state: InstantProductsState | null;
    methods?: Omit<InstantProducts, "state" | "subscribe"> | null;
}
export interface NotifyTriggerHook {
    state: NotifyTriggerState | null;
    methods?: Omit<NotifyTrigger, "state" | "subscribe"> | null;
}
export interface PaginationHook {
    state: PaginationState | null;
    methods?: Omit<Pagination, "state" | "subscribe"> | null;
}
export interface ProductViewHook {
    state: ProductViewState | null;
    methods?: Omit<ProductView, "state" | "subscribe"> | null;
}
export interface QueryTriggerHook {
    state: QueryTriggerState | null;
    methods?: Omit<QueryTrigger, "state" | "subscribe"> | null;
}
export interface RecentQueriesListHook {
    state: RecentQueriesState | null;
    methods?: Omit<RecentQueriesList, "state" | "subscribe"> | null;
}
export interface RedirectionTriggerHook {
    state: RedirectionTriggerState | null;
    methods?: Omit<RedirectionTrigger, "state" | "subscribe"> | null;
}
export interface SearchBoxHook {
    state: SearchBoxState | StandaloneSearchBoxState | null;
    methods?: Omit<SearchBox | StandaloneSearchBox, "state" | "subscribe"> | null;
}
export interface SortHook {
    state: SortState | null;
    methods?: Omit<Sort, "state" | "subscribe"> | null;
}
export interface SummaryHook {
    state: SummaryState | null;
    methods?: Omit<Summary, "state" | "subscribe"> | null;
}
export interface FacetGeneratorHook {
    state: FacetGeneratorState | null;
    methods?: Omit<FacetGenerator, "state" | "subscribe"> | null;
}
export interface BreadcrumbManagerHook {
    state: BreadcrumbManagerState | null;
    methods?: Omit<BreadcrumbManager, "state" | "subscribe"> | null;
}
export interface ParameterManagerHook {
    state: ParameterManagerState | null;
    methods?: Omit<ParameterManager<any>, "state" | "subscribe"> | null;
}
export interface FilterSuggestionsGeneratorHook {
    state: FilterSuggestionsState | null;
    methods?: Omit<FilterSuggestionsGenerator, "state" | "subscribe"> | null;
}

export interface HasCoveoHooks {
    cart: CartHook;
    context: ContextHook;
    productList: ProductListHook;
    didYouMean: DidYouMeanHook;
    instantProducts: InstantProductsHook;
    notifyTrigger: NotifyTriggerHook;
    pagination: PaginationHook;
    productView: ProductViewHook;
    queryTrigger: QueryTriggerHook;
    recentQueriesList: RecentQueriesListHook;
    redirectionTrigger: RedirectionTriggerHook;
    searchBox: SearchBoxHook;
    sort: SortHook;
    summary: SummaryHook;
    facetGenerator: FacetGeneratorHook;
    breadcrumbManager: BreadcrumbManagerHook;
    parameterManager: ParameterManagerHook;
    filterSuggestionsGenerator: FilterSuggestionsGeneratorHook;
}

// Default context
const defaultHooks: HasCoveoHooks = {
    cart: { state: null, methods: null },
    context: { state: null, methods: null },
    productList: { state: null, methods: null },
    didYouMean: { state: null, methods: null },
    instantProducts: { state: null, methods: null },
    notifyTrigger: { state: null, methods: null },
    pagination: { state: null, methods: null },
    productView: { state: null, methods: null },
    queryTrigger: { state: null, methods: null },
    recentQueriesList: { state: null, methods: null },
    redirectionTrigger: { state: null, methods: null },
    searchBox: { state: null, methods: null },
    sort: { state: null, methods: null },
    summary: { state: null, methods: null },
    facetGenerator: { state: null, methods: null },
    breadcrumbManager: { state: null, methods: null },
    parameterManager: { state: null, methods: null },
    filterSuggestionsGenerator: { state: null, methods: null },
};

// Context creation
const CoveoHooksContext = React.createContext<HasCoveoHooks>(defaultHooks);

type Props = ReturnType<typeof mapStateToProps>;

// Context builder
const createCoveoHooks = (state: CustomApplicationState): HasCoveoHooks => {
    const coveoCommerce = state.ADIGlobalData.coveoCommerce;
    if (isCoveoLoading(coveoCommerce)) return defaultHooks;

    const {
        useCart,
        useContext,
        useProductList,
        useDidYouMean,
        useInstantProducts,
        useNotifyTrigger,
        usePagination,
        useProductView,
        useQueryTrigger,
        useRecentQueriesList,
        useRedirectionTrigger,
        useSearchBox,
        useSort,
        useStandaloneSearchBox,
        useSummary,
        useFacetGenerator,
        useBreadcrumbManager,
        useParameterManager,
        useFilterSuggestionsGenerator,
    } = coveoCommerce.engineDefinition.controllers;

    return {
        cart: coveoCommerce.staticState?.controllers.cart ? useCart() : defaultHooks.cart,
        context: coveoCommerce.staticState?.controllers.context ? useContext() : defaultHooks.context,
        productList: coveoCommerce.staticState?.controllers.productList ? useProductList() : defaultHooks.productList,
        didYouMean: coveoCommerce.staticState?.controllers.didYouMean ? useDidYouMean() : defaultHooks.didYouMean,
        instantProducts: coveoCommerce.staticState?.controllers.instantProducts
            ? useInstantProducts()
            : defaultHooks.instantProducts,
        notifyTrigger: coveoCommerce.staticState?.controllers.notifyTrigger
            ? useNotifyTrigger()
            : defaultHooks.notifyTrigger,
        pagination: coveoCommerce.staticState?.controllers.pagination ? usePagination() : defaultHooks.pagination,
        productView: coveoCommerce.staticState?.controllers.productView ? useProductView() : defaultHooks.productView,
        queryTrigger: coveoCommerce.staticState?.controllers.queryTrigger
            ? useQueryTrigger()
            : defaultHooks.queryTrigger,
        recentQueriesList: coveoCommerce.staticState?.controllers.recentQueriesList
            ? useRecentQueriesList()
            : defaultHooks.recentQueriesList,
        redirectionTrigger: coveoCommerce.staticState?.controllers.redirectionTrigger
            ? useRedirectionTrigger()
            : defaultHooks.redirectionTrigger,
        searchBox: coveoCommerce.staticState?.controllers.searchBox
            ? useSearchBox()
            : coveoCommerce.staticState?.controllers.standaloneSearchBox
            ? useStandaloneSearchBox()
            : defaultHooks.searchBox,
        sort: coveoCommerce.staticState?.controllers.sort ? useSort() : defaultHooks.sort,
        summary: coveoCommerce.staticState?.controllers.summary ? useSummary() : defaultHooks.summary,
        facetGenerator: coveoCommerce.staticState?.controllers.facetGenerator
            ? useFacetGenerator()
            : defaultHooks.facetGenerator,
        breadcrumbManager: coveoCommerce.staticState?.controllers.breadcrumbManager
            ? useBreadcrumbManager()
            : defaultHooks.breadcrumbManager,
        parameterManager: coveoCommerce.staticState?.controllers.parameterManager
            ? useParameterManager()
            : defaultHooks.parameterManager,
        filterSuggestionsGenerator: coveoCommerce.staticState?.controllers.filterSuggestionsGenerator
            ? useFilterSuggestionsGenerator()
            : defaultHooks.filterSuggestionsGenerator,
    };
};

// HOC
export function withCoveoHooks<P extends HasCoveoHooks>(Component: React.ComponentType<P>) {
    type WrappedProps = Omit<P, keyof HasCoveoHooks> & Props;

    const WrappedComponent = (props: WrappedProps) => {
        const { state, ...rest } = props;
        const hooks = createCoveoHooks(state);

        return (
            <CoveoHooksContext.Provider value={hooks}>
                <Component {...(rest as any)} {...hooks} />
            </CoveoHooksContext.Provider>
        );
    };

    return connect(mapStateToProps)(WrappedComponent as React.ComponentType<any>);
}

// Hook
export const useCoveoHooks = (): HasCoveoHooks => {
    return useContext(CoveoHooksContext);
};
